import {
    HTTP_INTERCEPTORS,
    HttpClient,
    provideHttpClient,
    withInterceptorsFromDi,
    withXhr
} from '@angular/common/http';
import {
    ApplicationConfig,
    importProvidersFrom,
    inject,
    provideAppInitializer,
    provideZonelessChangeDetection
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { provideRouter } from '@angular/router';
import { provideStore } from '@ngxs/store';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { PagesState } from './shared/state/pages/pages.state';
import {
    MSAL_GUARD_CONFIG,
    MSAL_INSTANCE,
    MSAL_INTERCEPTOR_CONFIG,
    MsalBroadcastService,
    MsalGuard,
    MsalGuardConfiguration,
    MsalInterceptor,
    MsalInterceptorConfiguration,
    MsalService
} from '@azure/msal-angular';
import { InteractionType, PublicClientApplication } from '@azure/msal-browser';
import { provideTranslateService, TranslateService } from '@ngx-translate/core';
import { LeafletModule } from '@bluehalo/ngx-leaflet';
import { LeafletMarkerClusterModule } from '@bluehalo/ngx-leaflet-markercluster';
import { catchError, map, take } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { UnauthorizedInterceptor } from './auth/services/unauthorized-interceptor.service';
import { MultiTranslateHttpLoader } from './core/multi-tranlation-loader.service';
import { PagesService } from './shared/services/pages.service';
import { TokenInjector } from './auth/services/token-injector.interceptor';
import { NewsService } from './shared/services/news.service';
import { SearchService } from './shared/services/search.service';
import { FileService } from './shared/services/file-service';
import { ThemeService } from './shared/services/theme.service';
import { SplitThenSortSearchResultsPipe } from './shared/pipes/search/split-then-sort-search-results.pipe';

export function createTranslateLoader(http: HttpClient): MultiTranslateHttpLoader {
    return new MultiTranslateHttpLoader(http, [
        { prefix: './assets/i18n/core/', suffix: '.json' },
        { prefix: './assets/i18n/cards/', suffix: '.json' },
        { prefix: './assets/i18n/tooltips/', suffix: '.json' }
    ]);
}

function msalInstanceFactory(): PublicClientApplication {
    return new PublicClientApplication(environment.msalConfig);
}

function msalInterceptorConfigFactory(): MsalInterceptorConfiguration {
    const protectedResourceMap = new Map<string, string[]>();
    for (const item of environment.apiConfig) {
        protectedResourceMap.set(`${item.uri}/*`, item.scopes);
    }
    return {
        interactionType: InteractionType.Redirect,
        protectedResourceMap
    };
}

function msalGuardConfigFactory(): MsalGuardConfiguration {
    return {
        interactionType: InteractionType.Redirect,
        authRequest: {
            scopes: [...environment.apiConfig[0].scopes]
        },
        loginFailedRoute: '/unauthorized'
    };
}

function initializeMsalFactory(msalService: MsalService): () => Observable<undefined> {
    return () =>
        msalService.handleRedirectObservable().pipe(
            map((): undefined => {
                const accounts = msalService.instance.getAllAccounts();
                if (accounts.length > 0) {
                    const activeAccount = msalService.instance.getActiveAccount() || accounts[0];
                    msalService.instance.setActiveAccount(activeAccount);
                }
                return undefined;
            }),
            catchError(error => {
                console.error('Error during MSAL initialization:', error);
                return of(undefined);
            }),
            take(1)
        );
}

export const appConfig: ApplicationConfig = {
    providers: [
        provideZonelessChangeDetection(),
        importProvidersFrom(
            BrowserModule,
            BrowserAnimationsModule,
            FormsModule,
            ReactiveFormsModule,
            LeafletModule,
            LeafletMarkerClusterModule
        ),
        ...provideTranslateService({
            loader: () => createTranslateLoader(inject(HttpClient))
        }),
        provideRouter(routes),
        provideHttpClient(withXhr(), withInterceptorsFromDi()),
        provideStore(
            [PagesState],
            { developmentMode: !environment.production, compatibility: { strictContentSecurityPolicy: true } },
            ...(!environment.production ? [withNgxsReduxDevtoolsPlugin()] : [])
        ),
        provideAppInitializer(() => {
            const msalService = inject(MsalService);
            return initializeMsalFactory(msalService)();
        }),
        provideAppInitializer(() => inject(ThemeService).initTheme()),
        {
            provide: MSAL_INSTANCE,
            useFactory: msalInstanceFactory
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: msalGuardConfigFactory
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: msalInterceptorConfigFactory
        },
        MsalService,
        MsalGuard,
        MsalBroadcastService,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: TokenInjector,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: UnauthorizedInterceptor,
            multi: true
        },
        // Shared services from SharedModule
        TranslateService,
        SplitThenSortSearchResultsPipe,
        NewsService,
        SearchService,
        FileService,
        PagesService
    ]
};
